import i18n from '@/i18n';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Ticket, Wallet, Sparkles, ShoppingBag, Clock,
  RefreshCw, Plus, ArrowDownRight, ArrowUpRight, AlertTriangle,
  CheckCircle2, ArrowRight, Zap,
} from 'lucide-react';
import LolodriveLayout, { KpiCard, SectionCard, Badge, fmtEUR } from '../components/LolodriveLayout';
import { FeaturedAuctionBanner } from '../components/auctions/FeaturedAuctionBanner';
import StripeCheckoutButton from '../components/StripeCheckoutButton';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { lolodriveAPI, authAPI } from '../services/api';
import { PassRelayHeader } from '../components/pass/PassRelayHeader';
import { RelayReviewPrompt } from '../components/pass/RelayReviewPrompt';
import { SlotRatingPrompt } from '../components/pass/SlotRatingPrompt';
import { SavedCartReminder } from '../components/pass/SavedCartReminder';
import QuickRechargeCards from '../components/pass/QuickRechargeCards';
import { PassQrCard } from '../components/lolodrive/PassQrCard';
import { RescheduleOrderDialog } from '../components/lolodrive/RescheduleOrderDialog';
import { OrderTimeline } from '../components/lolodrive/OrderTimeline';
import { LoyaltyCard } from '../components/pass/LoyaltyCard';
import { toast } from 'sonner';

export default function PassSpacePage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [rescheduleOrder, setRescheduleOrder] = useState(null);
  const [savings, setSavings] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [referral, setReferral] = useState(null);
  const [claimCode, setClaimCode] = useState('');
  const [autoRenewBusy, setAutoRenewBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [selectedPack, setSelectedPack] = useState('STANDARD');

  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      navigate('/connexion', { replace: true });
      return;
    }
    load();
  }, [navigate]);

  const load = async () => {
    try {
      setLoading(true);
      const [pass, wallet, o, sv, ref] = await Promise.all([
        lolodriveAPI.myPass(),
        lolodriveAPI.myWallet(),
        lolodriveAPI.myOrders(),
        lolodriveAPI.mySavings().catch(() => null),
        lolodriveAPI.getMyReferralCode().catch(() => null),
      ]);
      setData({ ...pass, wallet: wallet.wallet });
      setLedger(wallet.ledger || []);
      setOrders(o.orders || []);
      setSavings(sv);
      setReferral(ref);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const recharge = async () => {
    try {
      const r = await lolodriveAPI.checkoutRecharge(window.location.origin, selectedPack);
      if (r?.url) {
        window.location.href = r.url;
      } else {
        toast.error('Erreur Stripe');
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remainingDays = () => {
    if (!data?.pass?.ends_at) return 0;
    return Math.max(0, Math.ceil((new Date(data.pass.ends_at) - new Date()) / (1000 * 60 * 60 * 24)));
  };

  const toggleAutoRenew = async () => {
    if (!data?.pass) return;
    setAutoRenewBusy(true);
    try {
      const r = await lolodriveAPI.setPassAutoRenew(!data.pass.is_auto_renew);
      toast.success(r.is_auto_renew ? 'Renouvellement automatique activé' : 'Renouvellement automatique désactivé');
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAutoRenewBusy(false);
    }
  };

  const reorder = (o) => {
    const cart = {};
    (o.items || []).forEach((it) => { if (it.sku && it.qty) cart[it.sku] = it.qty; });
    if (!Object.keys(cart).length) return toast.error('Aucun article à recommander');
    try { localStorage.setItem('kdm_lolodrive_cart', JSON.stringify(cart)); } catch { /* quota */ }
    toast.success('Panier rempli avec les articles de votre commande ✓');
    navigate('/catalogue-lolodrive');
  };

  const claimReferral = async () => {
    if (!claimCode.trim()) return;
    try {
      const r = await lolodriveAPI.claimReferralCode(claimCode.trim());
      toast.success(`Code accepté ! ${r.bonus_uc_each} UC crédités sur votre wallet.`);
      setClaimCode('');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const copyReferral = () => {
    if (!referral?.code) return;
    navigator.clipboard.writeText(referral.code).then(
      () => toast.success(`Code ${referral.code} copié`),
      () => toast.error('Copie impossible')
    );
  };

  const days = remainingDays();
  const expirationLevel = days <= 3 ? 'critical' : days <= 7 ? 'warning' : 'ok';

  return (
    <LolodriveLayout
      title={i18n.t('pass.mon_espace_pass')}
      subtitle={i18n.t('pass.pass_vie_chere_wallet')}
      actions={
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
        </Button>
      }
    >
      {loading && <div className="text-center text-white/50 py-12">{i18n.t('pass.chargement')}</div>}
      {!loading && data && (
        <>
          <PassRelayHeader />
          <RelayReviewPrompt />
          <SlotRatingPrompt />
          <SavedCartReminder />
          <FeaturedAuctionBanner className="mb-6" />
          {/* Hero PASS state */}
          <SectionCard className="mb-6 relative overflow-hidden">
            <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full blur-3xl opacity-30"
              style={{ background: data.active ? '#D9B35A' : '#666' }} />
            {data.active ? (
              <div className="relative flex flex-wrap gap-6 items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge color="#D9B35A">{i18n.t('pass.pass_vie_chere_actif')}</Badge>
                    {expirationLevel === 'critical' && (
                      <Badge color="#ef4444">
                        <AlertTriangle className="w-3 h-3 mr-1 inline" />
                        {i18n.t('pass.expire_bientot')}
                      </Badge>
                    )}
                  </div>
                  <div className="mt-3 text-5xl font-bold tracking-tight">
                    {data.wallet.balance_uc}
                    <span className="text-base text-white/40 font-normal ml-2">{i18n.t('pass.uc_disponibles')}</span>
                  </div>
                  <div className="text-sm mt-2 flex items-center gap-3 flex-wrap">
                    <span className={
                      expirationLevel === 'critical' ? 'text-red-400 font-semibold' :
                      expirationLevel === 'warning' ? 'text-amber-400 font-semibold' :
                      'text-white/50'
                    }>
                      <Clock className="w-3 h-3 inline mr-1" />
                      Expire dans {days} jours
                    </span>
                    <span className="text-white/40">
                      ({new Date(data.pass.ends_at).toLocaleDateString(i18n.language)})
                    </span>
                  </div>
                  <div className="text-xs text-white/40 mt-2 max-w-md">
                    {data.pass.is_auto_renew ? (
                      <>{i18n.t('pass.renouvellement_automatique')}<strong className="text-emerald-400">{i18n.t('pass.active')}</strong>{i18n.t('pass.a_l_expiration_un')}</>
                    ) : (
                      <>{i18n.t('pass.pas_de_renouvellement_automatique')}</>
                    )}
                  </div>
                  <div className="mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleAutoRenew}
                      disabled={autoRenewBusy}
                      data-testid="toggle-auto-renew-btn"
                      className="text-xs"
                    >
                      <RefreshCw className="w-3 h-3 mr-1.5" />
                      {data.pass.is_auto_renew ? 'Désactiver le renouvellement auto' : 'Activer le renouvellement auto'}
                    </Button>
                  </div>
                </div>
                <PassQrCard passId={data.pass.id} />
                <div className="flex gap-2 flex-wrap">
                  <Button asChild variant="outline" data-testid="goto-catalogue-btn">
                    <Link to="/catalogue-lolodrive">
                      <ShoppingBag className="w-4 h-4 mr-2" /> Voir le catalogue
                    </Link>
                  </Button>
                  <Dialog open={rechargeOpen} onOpenChange={setRechargeOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="open-recharge-btn"
                        style={{ background: 'linear-gradient(135deg, #D9B35A, #7c3aed)' }}>
                        <Plus className="w-4 h-4 mr-2" /> Recharger UC
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#15151c] border-white/10 text-white">
                      <DialogHeader>
                        <DialogTitle>{i18n.t('pass.recharger_mon_wallet_uc')}</DialogTitle>
                      </DialogHeader>
                      <p className="text-xs text-white/50 mb-2">
                        {i18n.t('pass.le_wallet_uc_est')}
                      </p>
                      <RadioGroup value={selectedPack} onValueChange={setSelectedPack} className="space-y-2">
                        {[
                          { id: 'MINI', label: i18n.t('pass.mini'), amount: '20 €', uc: '200 UC' },
                          { id: 'STANDARD', label: i18n.t('pass.standard'), amount: '40 €', uc: '400 UC' },
                          { id: 'MAXI', label: i18n.t('pass.maxi'), amount: '70 €', uc: '720 UC ✨', bonus: true },
                        ].map((p) => (
                          <label
                            key={p.id}
                            htmlFor={`pack-${p.id}`}
                            className="flex items-center gap-3 p-3 rounded-lg border border-white/10 hover:border-[#D9B35A]/40 cursor-pointer"
                            data-testid={`pack-${p.id}`}
                          >
                            <RadioGroupItem id={`pack-${p.id}`} value={p.id} />
                            <div className="flex-1">
                              <div className="font-medium flex items-center gap-2">
                                {p.label}
                                {p.bonus && <Badge color="#D9B35A">{i18n.t('pass.bonus_20_uc')}</Badge>}
                              </div>
                              <div className="text-xs text-white/40">{p.amount} → {p.uc}</div>
                            </div>
                          </label>
                        ))}
                      </RadioGroup>
                      <Button onClick={recharge} data-testid="confirm-recharge-btn"
                        style={{ background: 'linear-gradient(135deg, #D9B35A, #7c3aed)' }}>
                        {i18n.t('pass.payer_par_cb_stripe')}
                      </Button>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            ) : (
              <div className="relative">
                <Badge color="#666">{i18n.t('pass.pass_inactif')}</Badge>
                <h2 className="text-2xl font-bold mt-3">{i18n.t('pass.activez_votre_pass_vie')}</h2>
                <p className="text-sm text-white/60 mt-1 mb-4 max-w-2xl">
                  <strong>60 € = 600 UC</strong>, valable 30 jours. Accès aux prix PASS sur les produits ESSENTIELS,
                  paiement en UC sur l'ensemble du catalogue. Sans renouvellement automatique : vous restez maître de votre engagement.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <StripeCheckoutButton
                    createSession={(origin) => lolodriveAPI.checkoutPass(origin)}
                    label={`${i18n.t('pass.activer_mon_pass', 'Activer mon PASS')} — ${i18n.t('pass.payer_60_par_cb')}`}
                    size="lg"
                    testId="activate-pass-stripe-btn"
                    icon={<Zap className="w-4 h-4 mr-2" />}
                  />
                </div>
                <p className="text-[11px] text-white/30 mt-3">
                  Paiement sécurisé Stripe — votre PASS est activé immédiatement après le paiement.
                </p>
              </div>
            )}
          </SectionCard>

          {/* Recharge express 1 clic */}
          {data.active && <QuickRechargeCards />}
          <LoyaltyCard />

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard testId="kpi-balance" label={i18n.t('pass.solde_uc')} value={data.wallet.balance_uc}
              sub={(data.wallet.quiet_bonus_total_uc || 0) > 0 ? `dont ${data.wallet.quiet_bonus_total_uc} UC créneaux calmes` : ''}
              icon={Wallet} accent="#D9B35A" />
            <KpiCard
              testId="kpi-savings"
              label={i18n.t('pass.economies_realisees')}
              value={fmtEUR(savings?.savings_cents || 0)}
              sub={savings ? `sur ${savings.essential_items} produit(s) essentiel(s)` : ''}
              icon={Sparkles}
              accent="#10b981"
            />
            <KpiCard testId="kpi-orders" label={i18n.t('orders.mes_commandes')} value={orders.length} sub={`${orders.filter(o => o.status === 'FULFILLED').length} ${i18n.t('pass.retirees')}`} icon={ShoppingBag} accent="#7c3aed" />
            <KpiCard
              testId="kpi-status"
              label={i18n.t('pass.statut_pass')}
              value={data.active ? 'ACTIF' : 'INACTIF'}
              sub={data.active ? `${days}j restants` : 'Activez pour économiser'}
              icon={Ticket}
              accent={data.active ? (expirationLevel === 'critical' ? '#ef4444' : '#10b981') : '#666'}
            />
          </div>

          {/* Wallet ledger */}
          <SectionCard
            title={i18n.t('pass.historique_du_wallet_uc')}
            action={
              <span className="flex items-center gap-2">
                {ledger.some((l) => l.reason === 'RELIABLE_CLIENT_BONUS') && (
                  <span data-testid="reliable-client-badge"
                    className="text-[11px] font-bold text-[#D9B35A] bg-[#D9B35A]/10 border border-[#D9B35A]/35 rounded-full px-2.5 py-0.5">
                    🏅 Client fiable
                  </span>
                )}
                <span className="text-xs text-white/40">{ledger.length} mouvement(s)</span>
              </span>
            }
            className="mb-6"
          >
            {ledger.length === 0 && (
              <div className="text-sm text-white/40 py-4 text-center">
                {i18n.t('pass.aucun_mouvement_encore_activez')}
              </div>
            )}
            <div className="space-y-2">
              {ledger.slice(0, 8).map((l) => (
                <div key={l.id} data-testid={`ledger-${l.id}`}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ background: l.type === 'CREDIT' ? '#10b98120' : '#ef444420' }}>
                      {l.type === 'CREDIT'
                        ? <ArrowUpRight className="w-4 h-4 text-emerald-400" />
                        : <ArrowDownRight className="w-4 h-4 text-red-400" />}
                    </div>
                    <div>
                      <div className="text-sm font-medium">{reasonLabel(l.reason)}</div>
                      <div className="text-xs text-white/40">
                        {new Date(l.created_at).toLocaleString(i18n.language)}
                      </div>
                    </div>
                  </div>
                  <div className={`font-bold text-sm ${l.type === 'CREDIT' ? 'text-emerald-400' : 'text-red-400'}`}>
                    {l.type === 'CREDIT' ? '+' : '-'}{l.amount_uc} UC
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Orders */}
          <SectionCard
            title={i18n.t('pass.mes_commandes_recentes')}
            action={
              <Link to="/catalogue-lolodrive" className="text-xs text-[#D9B35A] hover:underline flex items-center gap-1" data-testid="goto-catalogue-link">
                + Nouvelle commande <ArrowRight className="w-3 h-3" />
              </Link>
            }
          >
            {orders.length === 0 && (
              <div className="text-sm text-white/40 py-8 text-center">
                {i18n.t('pass.aucune_commande_pour_le')}
                <div className="mt-2">
                  <Button asChild size="sm" variant="outline">
                    <Link to="/catalogue-lolodrive">{i18n.t('pass.decouvrir_le_catalogue')}</Link>
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {orders.slice(0, 8).map((o, idx) => (
                <div key={o.id} data-testid={`order-${o.id}`}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#7c3aed]/20 shrink-0">
                      <ShoppingBag className="w-4 h-4 text-[#a78bfa]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium font-mono truncate">{o.order_number}</div>
                      <div className="text-xs text-white/40">
                        {o.fulfillment_type} · {i18n.t('orders.articles_count', { count: o.items?.length || 0 })} ·{' '}
                        {new Date(o.created_at).toLocaleDateString(i18n.language)}
                      </div>
                      {o.pickup_slot_label && (
                        <div className="text-[10px] text-[#D9B35A]" data-testid={`order-slot-${o.id}`}>
                          🕐 {o.pickup_date ? `${new Date(o.pickup_date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })} · ` : ''}{o.pickup_slot_label}{(o.slot_fee_uc || 0) > 0 ? ` · frais ${o.slot_fee_uc} UC` : ''}
                          {['PAID', 'PREPARING', 'READY', 'DRAFT', 'PENDING_PAYMENT'].includes(o.status) && (
                            <button type="button" onClick={() => setRescheduleOrder(o)}
                              data-testid={`reschedule-btn-${o.id}`}
                              className="ml-2 underline decoration-dotted text-[#E9CF8E] hover:text-white">
                              Déplacer
                            </button>
                          )}
                        </div>
                      )}
                      <OrderTimeline order={o} />
                      {['PAID', 'PREPARING', 'READY'].includes(o.status) && o.pickup_slot_label && (
                        <a href={`https://wa.me/?text=${encodeURIComponent(
                          `Bonjour ! Peux-tu récupérer ma commande LOLODRIVE ${o.order_number} ? ` +
                          `${o.pickup_date ? `Le ${new Date(o.pickup_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}, ` : ''}` +
                          `créneau ${o.pickup_slot_label}. Statut actuel : ${o.status === 'READY' ? 'prête au retrait' : 'en préparation'}. Merci !`)}`}
                          target="_blank" rel="noopener noreferrer"
                          data-testid={`share-order-${o.id}`}
                          className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold text-[#25D366] hover:underline">
                          <svg viewBox="0 0 24 24" className="w-3 h-3 fill-current"><path d="M17.5 14.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.7.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.82 9.82 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88a9.83 9.83 0 0 1 7 2.9 9.82 9.82 0 0 1 2.89 7c0 5.45-4.44 9.87-9.9 9.87M20.47 3.55A11.8 11.8 0 0 0 12.05 0C5.5 0 .16 5.33.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.31-1.65a11.9 11.9 0 0 0 5.68 1.44h.01c6.55 0 11.89-5.33 11.89-11.89 0-3.18-1.24-6.16-3.48-8.4"/></svg>
                          Confier le retrait à un proche
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-sm">{fmtEUR(o.total_cents)}</div>
                    {(o.promo_discount_cents || 0) > 0 && (
                      <div className="text-[10px] font-semibold text-[#FF9E7A]" data-testid={`order-promo-discount-${o.id}`}>
                        ⚡ promo −{fmtEUR(o.promo_discount_cents)}
                      </div>
                    )}
                    <div className="flex gap-1 justify-end mt-0.5">
                      {(o.quiet_slot_bonus_uc || 0) > 0 && (
                        <Badge color="#10b981">+{o.quiet_slot_bonus_uc} UC calme{o.quiet_bonus_credited ? ' ✓' : ''}</Badge>
                      )}
                      {o.pay_with_uc && <Badge color="#D9B35A">UC</Badge>}
                      <Badge color={statusColor(o.status)}>{o.status}</Badge>
                    </div>
                    {idx === 0 && (o.items?.length || 0) > 0 && (
                      <Button size="sm" variant="outline" onClick={() => reorder(o)}
                        data-testid="reorder-btn" className="mt-2 text-xs h-7">
                        <RefreshCw className="w-3 h-3 mr-1.5" /> Recommander en 1 clic
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          <RescheduleOrderDialog order={rescheduleOrder} open={Boolean(rescheduleOrder)}
            onOpenChange={(v) => { if (!v) setRescheduleOrder(null); }} onDone={load} />

          {/* Parrainage coopérateur */}
          {referral && (
            <SectionCard
              title={i18n.t('pass.parrainage_cooperateur')}
              action={<Badge color="#7c3aed">+{referral.bonus_uc_per_use} UC parrain & filleul</Badge>}
              data-testid="referral-section"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-lg p-4 bg-[#7c3aed]/[0.08] border border-[#7c3aed]/30">
                  <div className="text-xs text-white/50 mb-1">{i18n.t('pass.mon_code')}</div>
                  <div className="flex items-center gap-2 mb-2">
                    <code data-testid="referral-code" className="text-lg font-mono font-bold text-[#a78bfa] tracking-wider">{referral.code}</code>
                    <Button size="sm" variant="outline" onClick={copyReferral} data-testid="copy-referral-btn" className="text-xs h-7">{i18n.t('pass.copier')}</Button>
                  </div>
                  <div className="text-xs text-white/50">
                    {i18n.t('pass.utilisations_label')} <strong>{referral.uses}/{referral.max_uses}</strong>{i18n.t('pass.chaque_filleul_vous_fait')}<strong>{referral.bonus_uc_per_use} UC</strong>.
                  </div>
                </div>
                <div className="rounded-lg p-4 bg-white/[0.03] border border-white/[0.08]">
                  <div className="text-xs text-white/50 mb-1">{i18n.t('pass.j_ai_un_code')}</div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={claimCode}
                      onChange={(e) => setClaimCode(e.target.value.toUpperCase())}
                      placeholder="KDM-XXXXXX"
                      data-testid="claim-code-input"
                      className="flex-1 px-3 py-2 text-sm bg-white/[0.04] border border-white/10 rounded-md font-mono uppercase"
                    />
                    <Button onClick={claimReferral} data-testid="claim-referral-btn"
                      style={{ background: 'linear-gradient(135deg, #D9B35A, #7c3aed)' }}>
                      {i18n.t('pass.activer')}
                    </Button>
                  </div>
                  <div className="text-[11px] text-white/40 mt-2">
                    Limite : un code par compte. Vous recevez {referral.bonus_uc_per_use} UC dès activation.
                  </div>
                </div>
              </div>
            </SectionCard>
          )}
        </>
      )}
    </LolodriveLayout>
  );
}

const reasonLabel = (r) => ({
  PASS_ACTIVATION: 'Activation PASS',
  PASS_ACTIVATION_DEMO: 'Activation PASS (démo)',
  RECHARGE: 'Recharge wallet',
  ORDER_PAY_UC: 'Paiement commande en UC',
  NO_PICKUP_PENALTY: 'Pénalité non-retrait',
  NO_PICKUP_PENALTY_REFUND: 'Remboursement pénalité (retrait effectué)',
  RELIABLE_CLIENT_BONUS: '🏅 Bonus Client Fiable',
  COUNTER_REFUND_UC: 'Remboursement retour article',
}[r] || r);

const statusColor = (s) => {
  if (s === 'FULFILLED') return '#10b981';
  if (s === 'READY') return '#D9B35A';
  if (s === 'PREPARING' || s === 'PAID') return '#3b82f6';
  if (s === 'CANCELLED' || s === 'REFUNDED') return '#ef4444';
  return '#7c3aed';
};
